/**
 * Team & access — tenant login staff (invite + role/status) at `/team`.
 * Warehouse `/users` is a different surface and is not used here.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  type ChipProps,
  type SelectChangeEvent,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorDetails } from '@/components/ErrorDetails';
import { getClaims } from '@/auth/authStore';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';
import {
  ASSIGNABLE_ROLES,
  cancelStaffInvitation,
  inviteStaff,
  listStaff,
  listStaffInvitations,
  resendStaffInvitation,
  setStaffRole,
  setStaffStatus,
  type AssignableRole,
  type SettableStatus,
  type StaffInvitation,
  type StaffMember,
} from '@/api/staff/staffApi';
import { formatStaffWriteError, isForbiddenStaffLoad } from '@/api/staff/staffErrors';
import type { JwtPayload } from '@/utils/jwt';

const EM_DASH = '—';
const SKELETON_ROWS = 5;

type ScreenTab = 'members' | 'invited';
type CopyFn = ReturnType<typeof useCopy>['t'];

/** A write failure shown inline; `error` is absent for pure client-side validation. */
interface InlineError {
  message: string;
  error?: unknown;
}

function tenantRoleOf(claims: JwtPayload | null): string {
  if (typeof claims?.tenant_role !== 'string') return '';
  return claims.tenant_role.replace(/^ROLE_/, '').trim();
}

function isSelfMember(member: StaffMember, claims: JwtPayload | null): boolean {
  if (!claims) return false;
  const claimEmail = typeof claims.email === 'string' ? claims.email.trim().toLowerCase() : '';
  if (claimEmail.length > 0 && member.email.trim().toLowerCase() === claimEmail) return true;
  const claimSub = typeof claims.sub === 'string' ? claims.sub.trim() : '';
  return claimSub.length > 0 && String(member.userId) === claimSub;
}

function parseTab(raw: string | null): ScreenTab {
  return raw === 'invited' ? 'invited' : 'members';
}

function formatInstant(iso: string | null, language: string): string {
  if (iso == null) return EM_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

function roleLabel(role: string, t: CopyFn): string {
  if (role === 'OWNER') return t('role.owner', 'Owner');
  if (role === 'ADMIN') return t('role.admin', 'Admin');
  if (role === 'STAFF') return t('role.staff', 'Staff');
  if (role === 'SUPER_ADMIN') return t('role.superAdmin', 'Super Admin');
  return role;
}

function statusLabel(status: string, t: CopyFn): string {
  if (status === 'ACTIVE') return t('team.status.ACTIVE', 'Active');
  if (status === 'DISABLED') return t('team.status.DISABLED', 'Disabled');
  if (status === 'INVITED') return t('team.status.INVITED', 'Invited');
  return status;
}

function statusChipColor(status: string): ChipProps['color'] {
  if (status === 'ACTIVE') return 'success';
  if (status === 'DISABLED') return 'default';
  if (status === 'INVITED') return 'info';
  return 'default';
}

function compareEmail(a: StaffMember, b: StaffMember): number {
  return a.email.localeCompare(b.email, undefined, { sensitivity: 'base' });
}

function SkeletonRows({ cols }: { cols: number }) {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TableRow key={`sk-${i}`}>
          <TableCell colSpan={cols}>
            <Skeleton variant="text" role="status" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export function TeamPage() {
  const { t } = useCopy();
  useDocumentTitle(t('page.teamAndAccess.title', 'Team & access'));
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  const claims = getClaims();
  const tenantRole = tenantRoleOf(claims);
  const isOwner = tenantRole === 'OWNER';
  const canListInvites = isOwner || tenantRole === 'SUPER_ADMIN';

  const roster = useResource(() => listStaff(), []);
  const [overrides, setOverrides] = useState<Map<number, StaffMember>>(new Map());
  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
    error?: unknown;
  } | null>(
    null,
  );
  const [addOpen, setAddOpen] = useState(false);
  const [invitedRefreshKey, setInvitedRefreshKey] = useState(0);

  const members = useMemo(() => {
    const base = roster.data ?? [];
    if (overrides.size === 0) return base;
    return base.map((row) => overrides.get(row.userId) ?? row);
  }, [roster.data, overrides]);

  const sortedMembers = useMemo(() => [...members].sort(compareEmail), [members]);

  const applyMemberRow = useCallback((row: StaffMember) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(row.userId, row);
      return next;
    });
  }, []);

  const handleRoleChange = useCallback(
    async (member: StaffMember, role: AssignableRole) => {
      setFeedback(null);
      try {
        const updated = await setStaffRole(member.userId, role);
        applyMemberRow(updated);
        setFeedback({
          severity: 'success',
          message: t('team.feedback.roleUpdated', 'Role updated.'),
        });
      } catch (err) {
        setFeedback({
          severity: 'error',
          message: formatStaffWriteError(err, t).message,
          error: err,
        });
      }
    },
    [applyMemberRow, t],
  );

  const handleStatusChange = useCallback(
    async (member: StaffMember, status: SettableStatus) => {
      setFeedback(null);
      try {
        const updated = await setStaffStatus(member.userId, status);
        applyMemberRow(updated);
        setFeedback({
          severity: 'success',
          message: t('team.feedback.statusUpdated', 'Status updated.'),
        });
      } catch (err) {
        setFeedback({
          severity: 'error',
          message: formatStaffWriteError(err, t).message,
          error: err,
        });
      }
    },
    [applyMemberRow, t],
  );

  const setTab = useCallback(
    (next: ScreenTab) => {
      setSearchParams(next === 'members' ? { tab: 'members' } : { tab: 'invited' });
    },
    [setSearchParams],
  );

  const forbidden = roster.status === 'error' && isForbiddenStaffLoad(roster.error);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Typography variant="h1" component="h1">
          {t('page.teamAndAccess.title', 'Team & access')}
        </Typography>
        {isOwner ? (
          <Button
            variant="contained"
            onClick={() => setAddOpen(true)}
            disabled={roster.status !== 'ready'}
          >
            {t('team.add', 'Add')}
          </Button>
        ) : null}
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, next: ScreenTab) => setTab(next)}
        aria-label={t('page.teamAndAccess.title', 'Team & access')}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab value="members" label={t('team.tab.members', 'Members')} />
        <Tab value="invited" label={t('team.tab.invited', 'Invited')} />
      </Tabs>

      {feedback ? (
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback(null)}
          sx={{ mb: 2 }}
          role="alert"
        >
          {feedback.message}
          {feedback.severity === 'error' && feedback.error !== undefined ? (
            <ErrorDetails error={feedback.error} headline={feedback.message} />
          ) : null}
        </Alert>
      ) : null}

      {tab === 'members' ? (
        <MembersPanel
          rosterStatus={roster.status}
          rosterError={roster.error}
          forbidden={forbidden}
          members={sortedMembers}
          isOwner={isOwner}
          claims={claims}
          language={i18n.language}
          t={t}
          onRetry={roster.refetch}
          onRoleChange={handleRoleChange}
          onStatusChange={handleStatusChange}
        />
      ) : (
        <InvitedPanel
          canListInvites={canListInvites}
          isOwner={isOwner}
          refreshKey={invitedRefreshKey}
          language={i18n.language}
          t={t}
        />
      )}

      {addOpen && isOwner ? (
        <AddStaffDialog
          members={members}
          t={t}
          onClose={() => setAddOpen(false)}
          onPromoted={(row) => {
            applyMemberRow(row);
            setAddOpen(false);
            setFeedback({
              severity: 'success',
              message: t('team.feedback.roleUpdated', 'Role updated.'),
            });
          }}
          onInvited={() => {
            setInvitedRefreshKey((n) => n + 1);
            setAddOpen(false);
            setFeedback({
              severity: 'success',
              message: t('team.feedback.inviteSent', 'Invitation sent.'),
            });
          }}
          onRefreshInvites={() => {
            setInvitedRefreshKey((n) => n + 1);
          }}
        />
      ) : null}
    </Box>
  );
}

function MembersPanel({
  rosterStatus,
  rosterError,
  forbidden,
  members,
  isOwner,
  claims,
  language,
  t,
  onRetry,
  onRoleChange,
  onStatusChange,
}: {
  rosterStatus: 'loading' | 'ready' | 'error';
  rosterError: unknown;
  forbidden: boolean;
  members: StaffMember[];
  isOwner: boolean;
  claims: JwtPayload | null;
  language: string;
  t: CopyFn;
  onRetry: () => void;
  onRoleChange: (member: StaffMember, role: AssignableRole) => Promise<void>;
  onStatusChange: (member: StaffMember, status: SettableStatus) => Promise<void>;
}) {
  if (rosterStatus === 'error' && forbidden) {
    return (
      <Alert severity="error" role="alert">
        <Typography variant="subtitle1">{t('team.forbiddenTitle', 'You don’t have access')}</Typography>
        <Typography variant="body2">
          {t(
            'team.forbiddenBody',
            'Team & access is limited to an owner, admin, or platform support session for this tenant.',
          )}
        </Typography>
        <ErrorDetails
          intent="read"
          error={rosterError}
          headline={t('team.forbiddenTitle', 'You don’t have access')}
        />
      </Alert>
    );
  }

  if (rosterStatus === 'error') {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      >
        <Typography variant="subtitle1">{t('team.loadError', 'Couldn’t load the team.')}</Typography>
        <Typography variant="body2">
          {t('team.loadErrorBody', 'This is not an empty list — please try again.')}
        </Typography>
        <ErrorDetails
          intent="read"
          error={rosterError}
          headline={t('team.loadError', 'Couldn’t load the team.')}
        />
      </Alert>
    );
  }

  const showActions = isOwner;
  const cols = showActions ? 6 : 5;

  return (
    <>
      <TableContainer>
        <Table aria-label={t('team.tab.members', 'Members')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('team.email', 'Email')}</TableCell>
              <TableCell>{t('team.name', 'Name')}</TableCell>
              <TableCell>{t('team.role', 'Role')}</TableCell>
              <TableCell>{t('team.status.label', 'Status')}</TableCell>
              <TableCell>{t('team.created', 'Created')}</TableCell>
              {showActions ? <TableCell align="right" /> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {rosterStatus === 'loading' ? <SkeletonRows cols={cols} /> : null}
            {rosterStatus === 'ready'
              ? members.map((member) => {
                  const self = isSelfMember(member, claims);
                  const canEdit = isOwner && !self && member.role !== 'OWNER';
                  return (
                    <TableRow key={member.userId}>
                      <TableCell>{member.email || EM_DASH}</TableCell>
                      <TableCell>{member.name ?? EM_DASH}</TableCell>
                      <TableCell>
                        <Chip size="small" label={roleLabel(member.role, t)} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={statusChipColor(member.status)}
                          label={statusLabel(member.status, t)}
                        />
                      </TableCell>
                      <TableCell>{formatInstant(member.createdAt, language)}</TableCell>
                      {showActions ? (
                        <TableCell align="right">
                          {canEdit ? (
                            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                              <Select
                                size="small"
                                value={ASSIGNABLE_ROLES.includes(member.role as AssignableRole)
                                  ? member.role
                                  : ''}
                                aria-label={t('team.role', 'Role')}
                                onChange={(event: SelectChangeEvent<string>) => {
                                  void onRoleChange(member, event.target.value as AssignableRole);
                                }}
                                sx={{ minWidth: 120 }}
                              >
                                {ASSIGNABLE_ROLES.map((role) => (
                                  <MenuItem key={role} value={role}>
                                    {roleLabel(role, t)}
                                  </MenuItem>
                                ))}
                              </Select>
                              {member.status === 'DISABLED' ? (
                                <Button
                                  size="small"
                                  onClick={() => void onStatusChange(member, 'ACTIVE')}
                                >
                                  {t('team.enable', 'Enable')}
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  onClick={() => void onStatusChange(member, 'DISABLED')}
                                >
                                  {t('team.disable', 'Disable')}
                                </Button>
                              )}
                            </Stack>
                          ) : null}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      {rosterStatus === 'ready' && members.length === 0 ? (
        <Typography sx={{ mt: 2 }}>{t('team.empty', 'No owner, admin, or staff login was returned for this tenant.')}</Typography>
      ) : null}
    </>
  );
}

function InvitedPanel({
  canListInvites,
  isOwner,
  refreshKey,
  language,
  t,
}: {
  canListInvites: boolean;
  isOwner: boolean;
  refreshKey: number;
  language: string;
  t: CopyFn;
}) {
  if (!canListInvites) {
    return (
      <Alert severity="info" role="status">
        {t('team.invited.adminHidden', 'Invitations can be managed by an owner.')}
      </Alert>
    );
  }

  return (
    <InvitedList isOwner={isOwner} refreshKey={refreshKey} language={language} t={t} />
  );
}

function InvitedList({
  isOwner,
  refreshKey,
  language,
  t,
}: {
  isOwner: boolean;
  refreshKey: number;
  language: string;
  t: CopyFn;
}) {
  const invites = useResource(() => listStaffInvitations(), [refreshKey]);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [replaced, setReplaced] = useState<Map<string, StaffInvitation>>(new Map());
  const [inlineError, setInlineError] = useState<InlineError | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const rows = useMemo(() => {
    return (invites.data ?? [])
      .filter((inv) => !removed.has(inv.invitationId))
      .map((inv) => ({ key: inv.invitationId, inv: replaced.get(inv.invitationId) ?? inv }));
  }, [invites.data, removed, replaced]);

  async function handleCancel(key: string, invitationId: string) {
    setInlineError(null);
    setBusyId(key);
    try {
      await cancelStaffInvitation(invitationId);
      setRemoved((prev) => new Set(prev).add(key));
    } catch (err) {
      const view = formatStaffWriteError(err, t);
      setInlineError({ message: view.message, error: err });
      if (view.refreshInvites) invites.refetch();
    } finally {
      setBusyId(null);
    }
  }

  async function handleResend(key: string, invitationId: string) {
    setInlineError(null);
    setBusyId(key);
    try {
      const fresh = await resendStaffInvitation(invitationId);
      setReplaced((prev) => new Map(prev).set(key, fresh));
    } catch (err) {
      const view = formatStaffWriteError(err, t);
      setInlineError({ message: view.message, error: err });
      if (view.refreshInvites) invites.refetch();
    } finally {
      setBusyId(null);
    }
  }

  if (invites.status === 'error') {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button color="inherit" size="small" onClick={invites.refetch}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      >
        {t('team.invited.loadError', 'Couldn’t load invitations.')}
        <ErrorDetails
          intent="read"
          error={invites.error}
          headline={t('team.invited.loadError', 'Couldn’t load invitations.')}
        />
      </Alert>
    );
  }

  const cols = isOwner ? 5 : 4;

  return (
    <>
      {inlineError ? (
        <Alert severity="error" role="alert" onClose={() => setInlineError(null)} sx={{ mb: 2 }}>
          {inlineError.message}
          {inlineError.error !== undefined ? (
            <ErrorDetails error={inlineError.error} headline={inlineError.message} />
          ) : null}
        </Alert>
      ) : null}
      <TableContainer>
        <Table aria-label={t('team.tab.invited', 'Invited')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('team.email', 'Email')}</TableCell>
              <TableCell>{t('team.role', 'Role')}</TableCell>
              <TableCell>{t('team.created', 'Created')}</TableCell>
              <TableCell>{t('team.expires', 'Expires')}</TableCell>
              {isOwner ? <TableCell align="right">{t('team.cancelInvite', 'Cancel')}</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {invites.status === 'loading' ? <SkeletonRows cols={cols} /> : null}
            {invites.status === 'ready'
              ? rows.map(({ key, inv }) => (
                  <TableRow key={key}>
                    <TableCell>{inv.email ?? EM_DASH}</TableCell>
                    <TableCell>
                      <Chip size="small" label={roleLabel(inv.role, t)} />
                    </TableCell>
                    <TableCell>{formatInstant(inv.createdAt, language)}</TableCell>
                    <TableCell>{formatInstant(inv.expiresAt, language)}</TableCell>
                    {isOwner ? (
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            disabled={busyId === key}
                            onClick={() => void handleCancel(key, inv.invitationId)}
                          >
                            {t('team.cancelInvite', 'Cancel')}
                          </Button>
                          <Button
                            size="small"
                            disabled={busyId === key}
                            onClick={() => void handleResend(key, inv.invitationId)}
                          >
                            {t('team.resend', 'Resend')}
                          </Button>
                        </Stack>
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      {invites.status === 'ready' && rows.length === 0 ? (
        <Typography sx={{ mt: 2 }}>{t('team.invited.empty', 'No pending invitations.')}</Typography>
      ) : null}
    </>
  );
}

function AddStaffDialog({
  members,
  t,
  onClose,
  onPromoted,
  onInvited,
  onRefreshInvites,
}: {
  members: StaffMember[];
  t: CopyFn;
  onClose: () => void;
  onPromoted: (row: StaffMember) => void;
  onInvited: () => void;
  onRefreshInvites: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<AssignableRole>('ADMIN');
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<InlineError | null>(null);

  const trimmedEmail = email.trim();
  const existing = useMemo(() => {
    if (trimmedEmail.length === 0) return undefined;
    return members.find((m) => m.email.trim().toLowerCase() === trimmedEmail.toLowerCase());
  }, [members, trimmedEmail]);

  async function handleSubmit() {
    setInlineError(null);
    if (trimmedEmail.length === 0) {
      setInlineError({
        message: t('team.errors.INVALID_REQUEST', 'Check the email and try again.'),
      });
      return;
    }
    setSubmitting(true);
    try {
      if (existing) {
        const updated = await setStaffRole(existing.userId, role);
        onPromoted(updated);
      } else {
        const trimmedName = name.trim();
        await inviteStaff({
          email: trimmedEmail,
          role,
          ...(trimmedName.length > 0 ? { name: trimmedName } : {}),
        });
        onInvited();
      }
    } catch (err) {
      const view = formatStaffWriteError(err, t);
      setInlineError({ message: view.message, error: err });
      if (view.refreshInvites) onRefreshInvites();
    } finally {
      setSubmitting(false);
    }
  }

  const submitLabel = existing
    ? t('team.updateRole', 'Update role')
    : t('team.sendInvite', 'Send invite');

  return (
    <Dialog open onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('team.addTitle', 'Add staff')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'team.addHelp',
              'Enter an email. If they already have a login, their role is updated; otherwise we send an invitation.',
            )}
          </Typography>
          <TextField
            label={t('team.email', 'Email')}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            fullWidth
            slotProps={{ htmlInput: { 'aria-label': t('team.email', 'Email') } }}
          />
          <TextField
            label={t('team.name', 'Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            fullWidth
            slotProps={{ htmlInput: { 'aria-label': t('team.name', 'Name') } }}
          />
          <FormControl>
            <FormLabel>{t('team.role', 'Role')}</FormLabel>
            <RadioGroup
              value={role}
              onChange={(e) => setRole(e.target.value as AssignableRole)}
            >
              {ASSIGNABLE_ROLES.map((r) => (
                <FormControlLabel
                  key={r}
                  value={r}
                  control={<Radio />}
                  label={roleLabel(r, t)}
                  disabled={submitting}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {inlineError ? (
            <Alert severity="error" role="alert">
              {inlineError.message}
              {inlineError.error !== undefined ? (
                <ErrorDetails error={inlineError.error} headline={inlineError.message} />
              ) : null}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
